import { useQuery } from "@tanstack/react-query";

import type { SubscriptionEstimate } from "features/subscriptions/types";

import { CUSTOMER_QUERY_KEYS } from "common/constants";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { post } from "common/helpers/HTTP";
import type { Pricing } from "common/types";

async function workspaceEstimateUpdateRequest(
  workspaceId: string,
  subscriptionId: string,
  planId: string,
  addonId: string,
  quantity: number,
  prorate: boolean,
): Promise<SubscriptionEstimate> {
  const { estimate } = await post(
    `workspaces/${workspaceId}/subscriptions/${subscriptionId}/estimates/update`,
    {
      items: [
        { id: planId },
        ...(quantity > 0 ? [{ id: addonId, quantity }] : []),
      ],
      prorate,
    },
  );
  return estimate;
}

async function customerEstimateUpdateRequest(
  planId: string,
  quantity: number,
  prorate: boolean,
): Promise<SubscriptionEstimate> {
  const { estimate } = await post("subscriptions/estimates/update", {
    item_price_id: planId,
    seat_count: quantity,
    prorate,
  });
  return estimate;
}

// if subscriptionId is true, uses the workspace endpoint
export default function useEstimateUpdate({
  pricing,
  quantity,
  isProrated,
  subscriptionId,
  isEnabled = true,
}: {
  pricing: { addon: Pricing; plan: Pricing } | undefined;
  quantity: number;
  isProrated: boolean;
  subscriptionId?: string;
  isEnabled?: boolean;
}) {
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const isCustomerSubscription = !subscriptionId;

  const {
    data: estimateUpdate,
    isFetching: isLoadingEstimateUpdate,
    error: estimateUpdateError,
  } = useQuery({
    queryKey: isCustomerSubscription
      ? CUSTOMER_QUERY_KEYS.subscriptionKeys.updateEstimate(
          pricing?.plan.id,
          quantity,
          isProrated,
        )
      : getQueryKeys(workspaceId).subscriptionKeys.updateEstimate(
          pricing?.plan.id,
          quantity,
          isProrated,
        ),
    queryFn: isCustomerSubscription
      ? () =>
          customerEstimateUpdateRequest(pricing?.plan.id, quantity, isProrated)
      : () =>
          workspaceEstimateUpdateRequest(
            workspaceId,
            subscriptionId,
            pricing?.plan.id,
            pricing?.addon.id,
            quantity,
            isProrated,
          ),
    enabled: !!pricing && isEnabled,
  });

  return { estimateUpdate, isLoadingEstimateUpdate, estimateUpdateError };
}
